/**
 * Shared "Add extinguisher" form used by the location detail page and the
 * extinguisher list page. Renders inside the shared <app-modal> (shadow DOM),
 * so it carries its own styles and produces exactly the payload the API expects.
 */
import { EXTINGUISHER_TYPES, EXTINGUISHER_AGENTS, DEFAULT_AGENT_FOR_CLASS } from './extinguisher-types.js';

export interface ExtinguisherFormOptions {
  /** Locations to choose from. When omitted the form hides the location field. */
  locations?: Array<{ id: string; name: string }>;
  /** Preselected / fixed location. */
  locationId?: string;
}

export interface ExtinguisherFormPayload {
  qr_code: string;
  location_id: string;
  extinguisher_type: string;
  /** Empty when the person adding the unit does not know; never guessed for them. */
  agent: string;
  size_lbs: number;
  room_area: string;
  install_date: string;
}

const FORM_STYLES = `
  .fg-form { display: grid; gap: 16px; }
  .fg-field { display: flex; flex-direction: column; gap: 6px; }
  .fg-field label { font-weight: 600; font-size: 14px; color: #111827; }
  .fg-field .req { color: #dc2626; margin-left: 2px; }
  .fg-field input, .fg-field select {
    padding: 10px 12px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 15px;
    font-family: inherit; background: #fff; color: #111827; width: 100%; box-sizing: border-box;
  }
  .fg-field input:focus, .fg-field select:focus { outline: none; border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,0.15); }
  .fg-help { font-size: 12.5px; color: #6b7280; line-height: 1.4; }
  .fg-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
  @media (max-width: 520px) { .fg-row { grid-template-columns: 1fr; } }
`;

function field(labelText: string, control: HTMLElement, help?: string, required = true): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'fg-field';
  const label = document.createElement('label');
  label.textContent = labelText;
  if (required) {
    const req = document.createElement('span');
    req.className = 'req';
    req.textContent = '*';
    label.appendChild(req);
  }
  if (control.id) label.htmlFor = control.id;
  wrap.appendChild(label);
  wrap.appendChild(control);
  if (help) {
    const h = document.createElement('small');
    h.className = 'fg-help';
    h.textContent = help;
    wrap.appendChild(h);
  }
  return wrap;
}

export function buildExtinguisherForm(opts: ExtinguisherFormOptions = {}): HTMLFormElement {
  const form = document.createElement('form');
  form.className = 'fg-form';
  form.noValidate = false;
  const style = document.createElement('style');
  style.textContent = FORM_STYLES;
  form.appendChild(style);

  if (opts.locations) {
    const select = document.createElement('select');
    select.name = 'location_id';
    select.id = 'fg-location';
    select.required = true;
    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = opts.locations.length ? 'Select a location' : 'No locations yet. Add one first.';
    select.appendChild(placeholder);
    for (const loc of opts.locations) {
      const o = document.createElement('option');
      o.value = loc.id;
      o.textContent = loc.name;
      if (loc.id === opts.locationId) o.selected = true;
      select.appendChild(o);
    }
    form.appendChild(field('Location', select));
  } else if (opts.locationId) {
    const hidden = document.createElement('input');
    hidden.type = 'hidden';
    hidden.name = 'location_id';
    hidden.value = opts.locationId;
    form.appendChild(hidden);
  }

  const qr = document.createElement('input');
  qr.type = 'text';
  qr.name = 'qr_code';
  qr.id = 'fg-qr';
  qr.required = true;
  qr.maxLength = 64;
  qr.autocomplete = 'off';
  qr.placeholder = 'e.g., CDC1-DOCK-01';
  form.appendChild(field('QR code / asset tag', qr, 'Your own numbering. It is printed on the label you stick beside the inspection tag.'));

  const type = document.createElement('select');
  type.name = 'extinguisher_type';
  type.id = 'fg-type';
  type.required = true;
  for (const t of EXTINGUISHER_TYPES) {
    const o = document.createElement('option');
    o.value = t.value;
    o.textContent = t.label;
    type.appendChild(o);
  }

  const size = document.createElement('input');
  size.type = 'number';
  size.name = 'size_lbs';
  size.id = 'fg-size';
  size.required = true;
  size.min = '0.5';
  size.step = '0.1';
  size.placeholder = 'e.g., 10';
  size.inputMode = 'decimal';

  const row = document.createElement('div');
  row.className = 'fg-row';
  row.appendChild(field('Fire class', type, 'What the unit is rated to put out. This drives the inspection schedule.'));
  row.appendChild(field('Agent charge (lbs)', size, 'Nameplate capacity, e.g. 5, 10, 20.'));
  form.appendChild(row);

  // The class does not tell you how the unit is checked. A B-rated CO2 cylinder
  // has no gauge and is verified by weight; a B-rated dry-chemical unit has one.
  // Optional, because a lot of existing inventories genuinely do not know.
  const agent = document.createElement('select');
  agent.name = 'agent';
  agent.id = 'fg-agent';
  for (const a of EXTINGUISHER_AGENTS) {
    const o = document.createElement('option');
    o.value = a.value;
    o.textContent = a.label;
    agent.appendChild(o);
  }
  // Follow the class while nobody has chosen for themselves; stop the moment they do.
  let agentTouched = false;
  agent.addEventListener('change', () => { agentTouched = true; });
  const suggestAgent = () => {
    if (agentTouched) return;
    const suggested = DEFAULT_AGENT_FOR_CLASS[type.value] ?? '';
    if (Array.from(agent.options).some((o) => o.value === suggested)) agent.value = suggested;
  };
  type.addEventListener('change', suggestAgent);
  suggestAgent();
  form.appendChild(field('Extinguishing agent', agent, 'Printed on the cylinder label. It decides whether the monthly check reads a gauge or weighs the unit.', false));

  const room = document.createElement('input');
  room.type = 'text';
  room.name = 'room_area';
  room.id = 'fg-room';
  room.required = true;
  room.maxLength = 120;
  room.placeholder = 'e.g., Dock door 4, Break room, Mezzanine east';
  form.appendChild(field('Room / area', room));

  const date = document.createElement('input');
  date.type = 'date';
  date.name = 'install_date';
  date.id = 'fg-date';
  date.required = true;
  date.value = localToday();
  date.max = date.value;
  form.appendChild(field('Date placed in service', date, 'Starts the monthly and annual clocks. Annual maintenance is recorded later as an annual inspection.'));

  // Whitespace-only values pass native "required"; reject them before submit.
  for (const input of [qr, room]) {
    input.addEventListener('input', () => input.setCustomValidity(input.value.trim() ? '' : 'Please enter a value.'));
  }

  return form;
}

/** Today's calendar date in the user's time zone as YYYY-MM-DD. */
export function localToday(now = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export function readExtinguisherForm(form: HTMLFormElement): ExtinguisherFormPayload {
  const fd = new FormData(form);
  return {
    qr_code: String(fd.get('qr_code') ?? '').trim(),
    location_id: String(fd.get('location_id') ?? ''),
    extinguisher_type: String(fd.get('extinguisher_type') ?? ''),
    agent: String(fd.get('agent') ?? ''),
    size_lbs: parseFloat(String(fd.get('size_lbs') ?? '')),
    room_area: String(fd.get('room_area') ?? '').trim(),
    install_date: String(fd.get('install_date') ?? ''),
  };
}
